import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FaStar } from "react-icons/fa";

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

const StarsBox = styled.span`
  position: relative;
  display: inline-flex;
  font-size: ${({ $size }) => $size};
  line-height: 1;
`;

const Row = styled.span`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(0.25)};
`;

const BaseRow = styled(Row)`
  color: ${({ theme }) => theme.colors.border.subtle};
`;

const FillRow = styled(Row)`
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.accent.warning};
  width: ${({ $pct }) => $pct}%;
`;

const ValueText = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
`;

/**
 * Accessible star rating display with smooth partial fill (a clipped overlay
 * row). Read-only; token colors; no motion. Pass `rating` (0-5) and optional
 * `count`. Renders a single img-role node with a bilingual aria-label.
 */
const RatingStars = ({ rating = 0, count, showValue = true, size = "0.95rem" }) => {
  const { t } = useTranslation();
  const safe = Math.max(0, Math.min(5, Number(rating) || 0));
  const pct = (safe / 5) * 100;
  const label = t("products.rating_aria", {
    defaultValue: "Rated {{rating}} out of 5",
    rating: safe.toFixed(1),
  });

  return (
    <Wrap role="img" aria-label={label}>
      <StarsBox $size={size} aria-hidden="true">
        <BaseRow>
          {[0, 1, 2, 3, 4].map((i) => (
            <FaStar key={i} />
          ))}
        </BaseRow>
        <FillRow $pct={pct}>
          {[0, 1, 2, 3, 4].map((i) => (
            <FaStar key={i} />
          ))}
        </FillRow>
      </StarsBox>
      {showValue && (
        <ValueText aria-hidden="true">
          {safe.toFixed(1)}
          {typeof count === "number" ? ` (${count})` : ""}
        </ValueText>
      )}
    </Wrap>
  );
};

export default RatingStars;
